import posthog from "posthog-js";
import { Capacitor } from "@capacitor/core";
import { APP_VERSION } from "@/lib/appVersion";
import { Sentry } from "@/lib/sentry";
import { posthogKeyProblem } from "@/lib/posthogKey";
import { sanitizeMirrorSpeechTelemetry } from "@/lib/mirrorSpeechTelemetry";
import type { MirrorSpeechTimingReport } from "@/lib/mirrorSpeechMetrics";
import { sanitizeMirrorProgressiveSpeechTelemetry, type MirrorProgressiveSpeechReport } from "@/lib/mirrorProgressiveSpeechTelemetry";

/**
 * Central, typed analytics helper (PostHog web SDK).
 *
 * Web-only by design: posthog-js runs inside the Capacitor WebView. No native
 * analytics plugin is used here — this module must stay 100% JS/web.
 *
 * Guarded init: when VITE_POSTHOG_KEY is absent OR fails format validation
 * (src/lib/posthogKey.ts — phc_ + tamanho plausível) every call is a no-op
 * and the disablement ANNOUNCES itself (console.warn + Sentry issue). Empty
 * template value and pasted placeholder both land in the same alarm branch.
 *
 * No PII: we never send email or name. Users are identified by their Supabase
 * user_id only. Person properties are limited to plan / language / has_avatar /
 * signup_provider. Session replay masks all inputs + sensitive text.
 *
 * Event names are snake_case and live ONLY in this file — call sites use the
 * typed `analytics.*` methods, never raw event strings.
 */

const KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
// EU host by default (LGPD/GDPR). Override via VITE_POSTHOG_HOST.
const HOST =
  (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ||
  "https://eu.i.posthog.com";

let ready = false;

// LGPD consent gate (Termly banner -> src/lib/consent.ts, wired in main.tsx).
// PostHog persists its own opt-out, so a Decline sticks across reloads even
// if the banner cache is cleared.
export function setAnalyticsConsent(granted: boolean): void {
  if (!ready) return;
  try {
    if (granted) {
      if (posthog.has_opted_out_capturing()) posthog.opt_in_capturing();
    } else if (!posthog.has_opted_out_capturing()) {
      posthog.opt_out_capturing();
    }
  } catch (e) {
    console.error("[analytics] consent toggle failed:", e);
  }
}

// ── Plataforma, com UMA régua ──────────────────────────────────────────────
// "ios" | "android" | "web" em TODO lugar (nativo = platform !== "web"): nos
// super properties da init, nos eventos do funil de cadastro e no metadata que
// o signUp grava no banco (Auth.tsx usa Capacitor.getPlatform() direto). Dois
// vocabulários para a mesma chave seriam a próxima armadilha de leitura.
export type AppPlatform = "ios" | "android" | "web";

/** Narrowing honesto do getPlatform(): string — sem cast calando o compilador. */
export function appPlatform(): AppPlatform {
  const p = Capacitor.getPlatform();
  return p === "ios" || p === "android" ? p : "web";
}

export function initAnalytics(): void {
  if (ready) return;
  // FORMATO, não só presença: o guard antigo era `if (!KEY)` e deixou passar
  // um placeholder com valor — COLE_A_CHAVE_AQUI entrou num .env de verdade
  // em 15/08, o init rodava, o PostHog descartava em silêncio e o alarme
  // abaixo nunca disparava (task b48e957d). Chave reprovada no formato cai no
  // MESMO ramo de alarme da ausente, com mensagem própria: quem lê o issue
  // sabe se falta preencher ou se o que preencheram não é chave.
  const problema = posthogKeyProblem(KEY);
  if (problema || !KEY) {
    // (o `|| !KEY` é redundante em runtime — chave vazia já é "missing" — e
    // existe para o narrowing do TS liberar o init abaixo sem cast)
    const caso = problema ?? "missing";
    const base =
      caso === "missing"
        ? "VITE_POSTHOG_KEY missing — PostHog disabled"
        : "VITE_POSTHOG_KEY invalid (não é phc_ com tamanho plausível) — PostHog disabled";
    // SEMPRE, não só em DEV: bundle sem key — ou com "chave" que não é chave
    // — é bundle CEGO, e o custo de este aviso existir só em dev foram três
    // semanas de nativo mudo sem ninguém saber (PR #476). No logcat / Web
    // Inspector, esta linha é o anúncio.
    console.warn("[analytics] " + base);
    // O warn só alcança quem tem cabo USB numa máquina; para os ~70-85% no
    // app ele não faz nada. O Sentry ENXERGA o nativo (provado 15/08 —
    // VYV-FRONTEND-3Q vive em https://localhost/mirror) e initSentry() roda
    // uma linha antes desta no main.tsx: a cegueira vira issue com contagem
    // de devices e release, não silêncio. Try/catch como os vizinhos, para o
    // anúncio da cegueira não virar a próxima falha.
    //
    // UMA VEZ POR DEVICE POR RELEASE, não por boot: a conta já perdeu 2.058
    // erros por Over Quota desde 05/08 — um anúncio por boot em ~2.300
    // aparelhos empurraria erro real para fora da quota (o alarme sobre
    // cegueira criando cegueira nova). O sentryDedupe NÃO serve aqui: o
    // DedupeState é um Map em memória com janela de 60s — morre com o boot,
    // que é exatamente a frequência a limitar. O carimbo guarda o SHA do
    // bundle: release nova cega anuncia de novo; a contagem de devices no
    // issue já diz o tamanho no primeiro evento de cada aparelho. O setItem
    // vem DEPOIS do capture: se o envio falhar, o próximo boot tenta de novo.
    try {
      const stamp = "vyv_analytics_key_missing_announced";
      if (localStorage.getItem(stamp) !== APP_VERSION) {
        const tags: Record<string, string> = { feature: "analytics_key_missing", reason: caso };
        // len sem o valor: 17 grita placeholder, 47 grita chave com typo —
        // diagnóstico remoto sem vazar um caractere do que foi colado.
        if (caso === "invalid") tags.key_len = String((KEY ?? "").length);
        Sentry.captureMessage(base, { level: "warning", tags });
        localStorage.setItem(stamp, APP_VERSION);
      }
    } catch { /* Sentry ou localStorage indisponíveis — o warn acima já saiu */ }
    return;
  }

  posthog.init(KEY, {
    api_host: HOST,
    before_send: event => sanitizeMirrorProgressiveSpeechTelemetry(sanitizeMirrorSpeechTelemetry(event, KEY), KEY),
    // Autocapture clicks / inputs / pageleaves automatically.
    autocapture: true,
    // SPA: react-router does not trigger native navigations, so we send
    // $pageview manually on route change (see AnalyticsBridge).
    capture_pageview: false,
    capture_pageleave: true,
    // Only build person profiles for identified (logged-in) users — keeps
    // anonymous traffic lightweight and privacy-friendly.
    person_profiles: "identified_only",
    persistence: "localStorage+cookie",
    // Session replay ON with strong masking — no PII leaks into recordings.
    disable_session_recording: false,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: "[data-ph-mask], [data-sensitive], input, textarea",
      maskInputOptions: {
        password: true,
        email: true,
        tel: true,
        color: false,
      },
    },
  });
  // Etiqueta ESTRUTURAL: platform e app_version viram super properties — todo
  // evento nasce com o recorte, em vez de inferência por $host (a régua que
  // falhou com 85% do produto no WebView nativo; user_consents 14d, 15/08).
  // register() NÃO ressuscita um bundle sem key: só etiqueta o que já chega.
  posthog.register({ platform: appPlatform(), app_version: APP_VERSION });
  ready = true;
}

// ── Identity ───────────────────────────────────────────────────────────────

export type SignupProvider = "email" | "google" | "apple";
export type PlanTier = "free" | "premium" | "pro" | "ultra";

export interface PersonProperties {
  plan?: PlanTier | string;
  language?: string;
  has_avatar?: boolean;
  signup_provider?: SignupProvider | string;
}

/** Call on login. Identifies by Supabase user_id (never email/name). */
export function identifyUser(userId: string, props?: PersonProperties): void {
  if (!ready) return;
  posthog.identify(userId, props as Record<string, unknown> | undefined);
}

/** Merge person properties without re-identifying (plan/language/has_avatar). */
export function setPersonProperties(props: PersonProperties): void {
  if (!ready) return;
  posthog.setPersonProperties(props as Record<string, unknown>);
}

/** Call on logout — clears the distinct_id so events de-associate. */
export function resetAnalytics(): void {
  if (!ready) return;
  posthog.reset();
}

/**
 * Best-effort flush before a full-page navigation (e.g. an OAuth redirect).
 * Events captured with `{ send_instantly: true }` dispatch immediately via a
 * keepalive request; this short wait gives that request time to leave the page
 * before the caller navigates away — closing the gap that dropped
 * `signup_started` on the web Google OAuth path (started << completed in prod).
 */
export function flushAnalytics(delayMs = 250): Promise<void> {
  if (!ready) return Promise.resolve();
  return new Promise((resolve) => window.setTimeout(resolve, delayMs));
}

/** Manual SPA pageview (autocapture pageview is disabled). */
export function capturePageview(path: string): void {
  if (!ready) return;
  posthog.capture("$pageview", {
    $current_url: window.location.href,
    path,
  });
}

// ── Typed event map ──────────────────────────────────────────────────────────

interface AnalyticsEventProps {
  fitness_workout_resumed: Record<string, never>;
  fitness_workout_completed: Record<string, never>;
  // Funnel
  app_opened: { app_version: string };
  onboarding_started: { onboarding_path?: string };
  language_selected: { language: string };
  signup_started: { provider: SignupProvider };
  signup_completed: { provider: SignupProvider };
  // Signup submit hit an email that already has an account (default-signup
  // trap: returning user with no persisted session, mostly in-app browsers).
  // auto_login_success = the typed password matched and we signed them in
  // silently; fallback_to_login = wrong password or OAuth-only account, so
  // the form flipped to sign-in mode. Sizes the leak that used to dead-end
  // in a raw "User already registered" toast (Sentry VYV-FRONTEND-2M).
  // NEVER accompanied by signup_completed / Meta CompleteRegistration.
  auth_signup_dedupe_to_login: {
    result: "auto_login_success" | "fallback_to_login";
    is_webview: boolean;
  };
  // ── Funil de confirmação de e-mail (task 1b643867) ──────────────────────
  // Só o provedor e-mail emite estes três. `signup_completed` segue saindo no
  // SUBMIT (a linha em auth.users nasce ali, confirmada ou não); o funil da
  // mitigação é completed → pending (e-mail enviado) → confirmed (primeira
  // sessão). `platform` existe porque o PostHog é CEGO ao WebView nativo por
  // $host (zero eventos com localhost em 14 dias, medido 15/08) — sem a
  // propriedade explícita, a fatia nativa do funil é imensurável e o
  // "reavaliar se a perda passar de 30%" fica sem denominador.
  signup_email_confirmation_pending: { platform: AppPlatform };
  signup_email_confirmation_resent: { platform: AppPlatform };
  signup_email_confirmed: { platform: AppPlatform };
  // Fires on the OAuth button CLICK (before the redirect), so the numerator of
  // the OAuth funnel exists even for users who never return with a session.
  // is_webview flags an in-app browser (Instagram/FB/TikTok/...) where the
  // redirect commonly breaks — split the funnel by it to size the webview leak.
  oauth_started: { provider: SignupProvider; is_native: boolean; is_webview: boolean };
  avatar_created: { avatar_type?: string; mode?: "onboarding" | "edit" };
  onboarding_completed: undefined;
  // Pre-login intro gate (SplashHero). `shown` = the cinematic actually played;
  // false = skipped by the SHOW_ONBOARDING_INTRO flag. Fired once per fresh/
  // re-show session at /auth so signup conversion can be compared by cohort.
  onboarding_intro: { shown: boolean };
  // Activation
  mirror_message_sent: { has_attachments: boolean };
  mirror_speech_latency: MirrorSpeechTimingReport;
  mirror_progressive_speech_latency: MirrorProgressiveSpeechReport;
  mirror_live_opened: undefined;
  language_live_opened: undefined;
  // First-session hook — value-question welcome bubble + one-tap reply chips.
  // Funnel: welcome_shown → welcome_chip_tap → mirror_message_sent.
  welcome_shown: undefined;
  welcome_chip_tap: { chip_id: string };
  // Permanent Mirror suggestion chips (30-prompt bank, 3 rotated per entry).
  // prompt_key is the i18n key (mirror.prompts.pN) so the funnel keeps a stable
  // id across locales.
  mirror_suggestion_tap: { prompt_key: string };
  // Language tutor (Modo Professor) — outcome of the day-set build, so empty/
  // failed word-sets are countable in prod (console is invisible on iPhone).
  language_dayset_result: {
    status: "ok" | "empty" | "error" | "timeout";
    count?: number;
    reason?: string;
    language?: string;
  };
  module_opened: { module: string };
  // Plan-tagged module open — fired on mount of the newly-FREE modules
  // (Brain / Fitness / Nutrition) to measure open/use by plan tier now that
  // they're no longer Premium-locked. `plan` is the live subscription tier.
  module_open_tier: { module: string; plan: string };
  checkin_completed: undefined;
  // Espelho Executor (F1). SERVER-emitted by the deliverable-generate edge
  // function for BOTH channels (app + whatsapp) — single source of truth,
  // including cost_usd. No client method on purpose; the entry lives here so
  // the event taxonomy stays centralized in this file. (PR-C: kind now spans
  // file deliverables and file_bytes was added server-side.)
  deliverable_generated: {
    channel: "app" | "whatsapp";
    kind: "text" | "document" | "sheet";
    requested_format: "text" | "docx" | "pdf" | "xlsx";
    clamped: boolean;
    language: string;
    model: string;
    has_module_data: boolean;
    is_free: boolean;
    tokens_in: number;
    tokens_out: number;
    cost_usd: number;
    file_bytes: number;
  };
  // SERVER-emitted pela edge `d` (link curto aivyv.com/d/{token}, PR-E) a
  // cada clique — fecha o funil gerado→aberto (o clique do WhatsApp era
  // cego). outcome: redirect = arquivo entregue; expired = página de link
  // expirado (deletado/fora da janela/sign fail). Sem método client — a
  // entrada vive aqui só pra taxonomia centralizada.
  deliverable_link_opened: {
    kind: string;
    format: string;
    channel: "app" | "whatsapp";
    days_since_created: number | null;
    outcome: "redirect" | "expired";
  };
  // Biblioteca (F1 PR-D) — loop de consumo do entregável: ver → baixar →
  // apagar. `source` em download distingue o chip do chat da Biblioteca;
  // `refreshed_url` = a signed URL original falhou e o util re-baixou
  // autenticado do bucket (mede quanto o fallback de expiração trabalha).
  library_viewed: { source: "drawer" | "direct" };
  deliverable_downloaded: {
    format: string;
    kind: string;
    source: "library" | "chip";
    refreshed_url: boolean;
  };
  deliverable_deleted: { format: string; kind: string };
  // Visualizar na Biblioteca (PR-F). `surface` diz COMO abriu: modal in-app
  // (txt/pdf web), native_browser (@capacitor/browser, Done nativo) ou
  // new_tab (fallback iOS Safari — object/iframe renderiza só a 1ª página).
  deliverable_viewed: {
    format: string;
    kind: string;
    source: "library";
    surface: "modal" | "native_browser" | "new_tab";
  };
  // Monetization
  paywall_viewed: { source: string };
  // Paywall funnel with placement attribution (daily-limit conversion test).
  // `paywall_viewed` above stays UNTOUCHED (historic metric). These three add
  // the placement dimension + the shown → cta_click | dismissed outcome split.
  // placement: "daily_limit" for the Free daily text cap; falls back to the
  // paywall variant name for placements not yet attributed.
  paywall_shown: { placement: string; variant: string };
  paywall_cta_click: { placement: string; cta: string };
  paywall_dismissed: { placement: string };
  plan_selected: { tier: PlanTier };
  // checkout_started closes the paywall_shown → cta_click → ? blind spot:
  // `source` = the paywall placement that led here (absent = organic entry
  // via /pricing or /creditos); tier "credits" + `pack_id` = the pack top-up
  // flow (CreditsPage), which emitted NOTHING before 12/07.
  checkout_started: { tier: PlanTier | "credits"; source?: string; pack_id?: string };
  // Client-observed payment confirmation (CheckoutPremium success stage /
  // CreditsPage poll seeing status=paid). Closes the funnel per placement:
  // paywall_shown → cta_click → checkout_started → checkout_completed.
  // `source` inherits the same navigation-state attribution as
  // checkout_started (absent = organic or post-reload resume, where the
  // state is gone). Server-side truth stays purchase_completed (webhook).
  checkout_completed: { tier: PlanTier | "credits"; source?: string; pack_id?: string };
  subscription_activated: { tier: PlanTier };
  // Referral (placeholders — wired when the referral feature ships)
  referral_shared: { channel?: string };
  referral_redeemed: { code?: string };
  // Landing (public marketing page at the web root — web only). `placement`
  // distinguishes the CTA position (nav / hero / footer) in the funnel.
  landing_view: undefined;
  landing_demo_interaction: {
    example: number;
    method: "category" | "next" | "previous";
  };
  // Fired once when the landing Pricing section scrolls into view. Distinct from
  // `paywall_viewed` (the in-app purchase paywall) on purpose — mixing the two
  // would corrupt the real-paywall conversion metric.
  landing_pricing_viewed: undefined;
  landing_connected_example: { example: "email" | "calendar" | "video" | "documents" };
  // Landing hero-proof carousel (8 slides). slide_viewed fires once per slide
  // per session at >=60% visibility (IntersectionObserver); interaction
  // distinguishes native swipe from the desktop arrows and the dots.
  landing_carousel_slide_viewed: { index: number; slide_id: string };
  landing_carousel_interaction: { type: "swipe" | "arrow" | "dot" };
  // Position-10 CTA card of the PT carousel — the end-of-carousel click.
  landing_carousel_end_cta_click: undefined;
  cta_download_click: { placement?: string };
  cta_webapp_click: { placement?: string };
  // Meus Agentes (PR-4b) — the retention bet. No PII; only template/family/
  // schedule/channel shape + the user's tier.
  agent_created: { template_id: string; family: string; schedule_type: string; channel: string; tier: string };
  agent_edited: { template_id: string; family: string; schedule_type: string; channel: string; tier: string };
  agent_paused: { template_id: string; family: string; tier: string };
  agent_resumed: { template_id: string; family: string; tier: string };
  agent_deleted: { template_id: string; family: string; tier: string };
  // Menu multitarefa do compositor (P2) — funil de descoberta.
  mirror_composer_menu_opened: undefined;
  mirror_composer_action: { item: string };
  // ── Item 2 · soft-ask de WhatsApp no D0 ──────────────────────────────────
  // Os cinco desfechos do experimento. `wa_soft_ask_gate` sai UMA vez por
  // decisão, nos DOIS braços; `$set` grava a person property que segmenta
  // retenção treatment × control nativo no PostHog. `reason` é `string | null`
  // e não `WaSoftAskSkipReason` de propósito: o call site já serializa, e
  // acoplar a taxonomia de analytics ao enum de controle de fluxo obrigaria a
  // mexer aqui a cada motivo novo.
  wa_soft_ask_gate: {
    arm: string;
    assigned_pct: number;
    shown: boolean;
    reason: string | null;
    exposure_id: string | null;
    $set: { wa_soft_ask_arm: string };
  };
  wa_soft_ask_accepted: { arm: string; exposure_id: string | null };
  wa_soft_ask_declined: { arm: string; exposure_id: string | null };
  wa_soft_ask_linked: { arm: string; exposure_id: string | null };
  wa_soft_ask_ignored: { arm: string; exposure_id: string | null };
}

// Options bag forwarded to posthog.capture (e.g. `{ send_instantly: true }`).
// Derived from the SDK so it never drifts from the installed posthog-js.
type CaptureOptions = Parameters<typeof posthog.capture>[2];

function capture<K extends keyof AnalyticsEventProps>(
  event: K,
  ...args: AnalyticsEventProps[K] extends undefined
    ? [props?: undefined, options?: CaptureOptions]
    : [props: AnalyticsEventProps[K], options?: CaptureOptions]
): void {
  if (!ready) return;
  posthog.capture(event, args[0] as Record<string, unknown> | undefined, args[1]);
}

/**
 * Typed event surface. Use these instead of raw strings so the event taxonomy
 * stays centralized and discoverable.
 */
export const analytics = {
  mirrorProgressiveSpeechLatency: (report: MirrorProgressiveSpeechReport) => {
    try { capture("mirror_progressive_speech_latency", report); } catch { /* Optional telemetry cannot break speech. */ }
  },
  mirrorSpeechLatency: (report: MirrorSpeechTimingReport) => {
    try { capture("mirror_speech_latency", report); } catch { /* Optional telemetry cannot break speech. */ }
  },
  // Funnel
  appOpened: () => capture("app_opened", { app_version: APP_VERSION }),
  onboardingStarted: (onboardingPath?: string) =>
    capture("onboarding_started", { onboarding_path: onboardingPath }),
  languageSelected: (language: string) =>
    capture("language_selected", { language }),
  signupStarted: (provider: SignupProvider, options?: CaptureOptions) =>
    capture("signup_started", { provider }, options),
  signupCompleted: (provider: SignupProvider) =>
    capture("signup_completed", { provider }),
  signupEmailConfirmationPending: (platform: AppPlatform) =>
    capture("signup_email_confirmation_pending", { platform }),
  signupEmailConfirmationResent: (platform: AppPlatform) =>
    capture("signup_email_confirmation_resent", { platform }),
  signupEmailConfirmed: (platform: AppPlatform) =>
    capture("signup_email_confirmed", { platform }),
  signupDedupeToLogin: (
    result: "auto_login_success" | "fallback_to_login",
    props: { isWebview: boolean },
  ) =>
    capture("auth_signup_dedupe_to_login", {
      result,
      is_webview: props.isWebview,
    }),
  oauthStarted: (
    provider: SignupProvider,
    props: { isNative: boolean; isWebview: boolean },
  ) =>
    capture("oauth_started", {
      provider,
      is_native: props.isNative,
      is_webview: props.isWebview,
    }),
  avatarCreated: (avatarType?: string, mode?: "onboarding" | "edit") =>
    capture("avatar_created", { avatar_type: avatarType, mode }),
  onboardingCompleted: () => capture("onboarding_completed"),
  onboardingIntro: (shown: boolean) => capture("onboarding_intro", { shown }),

  // Activation
  mirrorMessageSent: (hasAttachments: boolean) =>
    capture("mirror_message_sent", { has_attachments: hasAttachments }),
  mirrorLiveOpened: () => capture("mirror_live_opened"),
  welcomeShown: () => capture("welcome_shown"),
  welcomeChipTap: (chipId: string) => capture("welcome_chip_tap", { chip_id: chipId }),
  mirrorSuggestionTap: (promptKey: string) =>
    capture("mirror_suggestion_tap", { prompt_key: promptKey }),
  // P2 — funil de descoberta do menu multitarefa do compositor.
  mirrorComposerMenuOpened: () => capture("mirror_composer_menu_opened"),
  mirrorComposerAction: (item: string) =>
    capture("mirror_composer_action", { item }),
  // ── Item 2 · soft-ask de WhatsApp no D0 ────────────────────────────────
  // UM evento nos DOIS braços, no ponto da decisão — mesmo padrão do
  // experimento memory.free_summaries_pct. `$set` grava a person property
  // para segmentar retenção treatment × control nativo no PostHog, e
  // `assigned_pct` vai junto porque com pct=0 a análise precisa filtrar
  // assigned_pct > 0 para ler só o período em que o experimento rodou.
  // `reason` é o que separa "não converteu" de "nunca viu".
  //
  // ── O QUE MUDOU, E POR QUÊ (11/08) ──────────────────────────────────────
  // `wa_soft_ask_gate` mediu 255 eventos com `shown=false` e 5 com true. O
  // número estava certo e mesmo assim não dava para agir: o evento saía no
  // primeiro instante em que havia um braço, quando a decisão ainda era
  // PROVISÓRIA, e congelava `push_owns_the_moment` como se fosse desfecho.
  // Agora ele só sai quando a decisão é final (isWaSoftAskDecisionFinal).
  //
  // Duas coisas que o desenho antigo não conseguia distinguir e agora
  // consegue:
  //  1. EXPOSIÇÃO ↔ DESFECHO. `exposure_id` é o mesmo em gate/accepted/
  //     declined/ignored/linked. Sem ele os desfechos eram eventos soltos com
  //     um `arm` — dava para contar, não para ligar ao que foi exposto, e
  //     "não converteu" ficava indistinguível de "nunca viu".
  //  2. TOCAR ≠ VINCULAR. `accepted` é o toque no botão; quem escreve o
  //     vínculo é o whatsapp-webhook, DEPOIS, e pode nunca escrever (o
  //     usuário abre o WhatsApp e não envia a mensagem). Tratar toque como
  //     conversão inflaria a métrica primária exatamente no lugar onde ela
  //     decide o experimento. `linked` é a confirmação real, relida quando o
  //     usuário volta do WhatsApp.
  waSoftAskGate: (p: {
    arm: string;
    assignedPct: number;
    shown: boolean;
    reason: string | null;
    exposureId: string | null;
  }) =>
    capture("wa_soft_ask_gate", {
      arm: p.arm,
      assigned_pct: p.assignedPct,
      shown: p.shown,
      reason: p.reason,
      exposure_id: p.exposureId,
      $set: { wa_soft_ask_arm: p.arm },
    }),
  waSoftAskAccepted: (arm: string, exposureId: string | null) =>
    capture("wa_soft_ask_accepted", { arm, exposure_id: exposureId }),
  waSoftAskDeclined: (arm: string, exposureId: string | null) =>
    capture("wa_soft_ask_declined", { arm, exposure_id: exposureId }),
  /** Vínculo CONFIRMADO (o webhook escreveu a linha), não o toque no botão. */
  waSoftAskLinked: (arm: string, exposureId: string | null) =>
    capture("wa_soft_ask_linked", { arm, exposure_id: exposureId }),
  /**
   * Visto e não respondido — o terceiro desfecho, que antes simplesmente não
   * existia. Sem ele o denominador de "quem viu" some junto com a aba, e
   * dispensa ativa vira indistinguível de indiferença.
   */
  waSoftAskIgnored: (arm: string, exposureId: string | null) =>
    capture("wa_soft_ask_ignored", { arm, exposure_id: exposureId }),
  languageLiveOpened: () => capture("language_live_opened"),
  languageDaySetResult: (
    status: "ok" | "empty" | "error" | "timeout",
    props?: { count?: number; reason?: string; language?: string },
  ) => capture("language_dayset_result", { status, ...props }),
  moduleOpened: (module: string) => capture("module_opened", { module }),
  // Plan-tagged open for the newly-FREE modules (Brain/Fitness/Nutrition).
  fitnessResume: () => capture("fitness_workout_resumed", {}),
  fitnessCompleted: () => capture("fitness_workout_completed", {}),
  moduleOpenTier: (module: string, plan: string) =>
    capture("module_open_tier", { module, plan }),
  checkinCompleted: () => capture("checkin_completed"),

  // Biblioteca (F1 PR-D)
  libraryViewed: (source: "drawer" | "direct") =>
    capture("library_viewed", { source }),
  deliverableDownloaded: (props: {
    format: string;
    kind: string;
    source: "library" | "chip";
    refreshedUrl: boolean;
  }) =>
    capture("deliverable_downloaded", {
      format: props.format,
      kind: props.kind,
      source: props.source,
      refreshed_url: props.refreshedUrl,
    }),
  deliverableDeleted: (props: { format: string; kind: string }) =>
    capture("deliverable_deleted", { format: props.format, kind: props.kind }),
  deliverableViewed: (props: {
    format: string;
    kind: string;
    // "chat" (PR-A F2): abertura do viewer pela thumbnail inline da bolha.
    surface: "modal" | "native_browser" | "new_tab" | "chat";
  }) =>
    capture("deliverable_viewed", {
      format: props.format,
      kind: props.kind,
      source: "library",
      surface: props.surface,
    }),

  // Monetization
  paywallViewed: (source: string) => capture("paywall_viewed", { source }),
  paywallShown: (placement: string, variant: string) =>
    capture("paywall_shown", { placement, variant }),
  paywallCtaClick: (placement: string, cta: string) =>
    capture("paywall_cta_click", { placement, cta }),
  paywallDismissed: (placement: string) =>
    capture("paywall_dismissed", { placement }),
  planSelected: (tier: PlanTier) => capture("plan_selected", { tier }),
  checkoutStarted: (tier: PlanTier | "credits", source?: string, packId?: string) =>
    capture("checkout_started", { tier, source, pack_id: packId }),
  checkoutCompleted: (tier: PlanTier | "credits", source?: string, packId?: string) =>
    capture("checkout_completed", { tier, source, pack_id: packId }),
  subscriptionActivated: (tier: PlanTier) =>
    capture("subscription_activated", { tier }),

  // Referral — TODO: wire `referralShared` to the share action and
  // `referralRedeemed` to the redemption handler once the referral feature
  // ships. No trigger exists in the codebase yet.
  referralShared: (channel?: string) =>
    capture("referral_shared", { channel }),
  referralRedeemed: (code?: string) =>
    capture("referral_redeemed", { code }),

  // Landing (public marketing page — web only)
  landingView: () => capture("landing_view"),
  // Fixed demo ids only. Never voice content or visitor-entered data.
  landingDemoInteraction: (example: number, method: "category" | "next" | "previous") =>
    capture("landing_demo_interaction", { example, method }),
  landingPricingViewed: () => capture("landing_pricing_viewed"),
  landingConnectedExample: (example: "email" | "calendar" | "video" | "documents") =>
    capture("landing_connected_example", { example }),
  landingCarouselSlideViewed: (index: number, slideId: string) =>
    capture("landing_carousel_slide_viewed", { index, slide_id: slideId }),
  landingCarouselInteraction: (type: "swipe" | "arrow" | "dot") =>
    capture("landing_carousel_interaction", { type }),
  landingCarouselEndCtaClick: () => capture("landing_carousel_end_cta_click"),
  ctaDownloadClick: (placement?: string) =>
    capture("cta_download_click", { placement }),
  ctaWebappClick: (placement?: string) =>
    capture("cta_webapp_click", { placement }),

  // Meus Agentes (PR-4b)
  agentCreated: (props: { templateId: string; family: string; scheduleType: string; channel: string; tier: string }) =>
    capture("agent_created", {
      template_id: props.templateId,
      family: props.family,
      schedule_type: props.scheduleType,
      channel: props.channel,
      tier: props.tier,
    }),
  agentEdited: (props: { templateId: string; family: string; scheduleType: string; channel: string; tier: string }) =>
    capture("agent_edited", {
      template_id: props.templateId,
      family: props.family,
      schedule_type: props.scheduleType,
      channel: props.channel,
      tier: props.tier,
    }),
  agentPaused: (props: { templateId: string; family: string; tier: string }) =>
    capture("agent_paused", { template_id: props.templateId, family: props.family, tier: props.tier }),
  agentResumed: (props: { templateId: string; family: string; tier: string }) =>
    capture("agent_resumed", { template_id: props.templateId, family: props.family, tier: props.tier }),
  agentDeleted: (props: { templateId: string; family: string; tier: string }) =>
    capture("agent_deleted", { template_id: props.templateId, family: props.family, tier: props.tier }),
};
